import { Component, OnInit, Inject } from '@angular/core';
import { JobTitle, Department, ExternalPartner, ServiceType, ExternalPartnerCertification } from 'app/profile/models/people.model';
import { Observable, forkJoin } from 'rxjs';
import { PeopleService } from 'app/profile/services/people.service';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CertificationType } from 'app/profile/models/profile.model';
import { ProfileService } from 'app/profile/services/profile.service';
import { TslDialog, TslOption } from '@tesla/ng-tds';
import { I18nService } from 'app/shared/services/i18n.service';
import { Attachment } from 'app/profile/models/generic.model';
import * as FileSaver from 'file-saver';
import { Country } from 'app/profile/models/masterdata.model';
import { SupplierProfileState } from 'app/profile/store/app.reducer';
import { Store, select } from '@ngrx/store';
import { SelectCountries } from 'app/profile/store/app.selector';
import { CertificationDialogComponent } from '../../site/certification-dialog/certification-dialog.component';
import { ExternalCertificationDialogComponent } from '../certification-dialog/external-certification-dialog.component';

@Component({
    templateUrl: './external-form-dialog.component.html',
    styleUrls: ['./external-form-dialog.component.scss']
})
export class ExternalFormDialogComponent implements OnInit {
    jobtitles: JobTitle[];
    departments: Department[];
    serviceTypes: ServiceType[];
    obCountries$: Observable<Country[]>;
    certificationTypes: CertificationType[];

    deleteTitle: string;
    deleteConfirmation: string;
    today = Date.now;
    newCertifications: ExternalPartnerCertification[] = [];
    externalForm: FormGroup;
    attachmentIndex: number;

    get company() { return this.externalForm.get('company'); }
    get servicetype() { return this.externalForm.get('servicetype'); }
    get jobtitle() { return this.externalForm.get('jobtitle'); }
    get department() { return this.externalForm.get('department'); }
    get address1() { return this.externalForm.get('address1'); }
    get address2() { return this.externalForm.get('address2'); }
    get city() { return this.externalForm.get('city'); }
    get state() { return this.externalForm.get('state'); }
    get zipcode() { return this.externalForm.get('zipcode'); }
    get country() { return this.externalForm.get('country'); }

    constructor(
        private fb: FormBuilder,
        private service: PeopleService,
        private profileService: ProfileService,
        private translate: I18nService,
        private dialog: TslDialog,
        private matDialog: MatDialog,
        private store: Store<SupplierProfileState>,
        public dialogRef: MatDialogRef<ExternalFormDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ExternalPartner) {
        this.createForm();
    }

    ngOnInit() {
        this.loadResources();

        if (this.data.id > 0) {
            this.externalForm.patchValue({
                company: this.data.companyName,
                servicetype: this.data.serviceType,
                jobtitle: this.data.jobTitle,
                department: this.data.department,
                address1: this.data.address1,
                address2: this.data.address2,
                city: this.data.city,
                state: this.data.state,
                zipcode: this.data.zipCode,
                country: this.data.countryId
            });
        }
    }

    onClose(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        if (this.externalForm.invalid) {
            return;
        }

        this.data.companyName = this.company.value;
        const service = this.serviceTypes.find(x => x.type === this.servicetype.value);
        this.data.serviceTypeId = service ? service.id : 0;
        this.data.serviceType = this.servicetype.value;

        const job = this.jobtitles.find(x => x.name === this.jobtitle.value);
        this.data.jobTitleId = job ? job.id : 0;
        this.data.jobTitle = this.jobtitle.value;

        const department = this.departments.find(x => x.name === this.department.value);
        this.data.departmentId = department ? department.id : 0;
        this.data.department = this.department.value;

        this.data.address1 = this.address1.value;
        this.data.address2 = this.address2.value;
        this.data.city = this.city.value;
        this.data.state = this.state.value;
        this.data.zipCode = this.zipcode.value;
        this.data.countryId = this.country.value;
        this.dialogRef.close(this.data);
    }

    onOpenCertification(): void {
        this.attachmentIndex = -1;
        const model = new ExternalPartnerCertification();
        model.supplierCode = this.data.supplierCode;
        this.openCertificationDialog(model);
    }

    onEditCertification(model: ExternalPartnerCertification, attachmentIndex: number): void {
        this.attachmentIndex = attachmentIndex;
        model.supplierCode = this.data.supplierCode;
        model.externalPartnerId = this.data.id;
        this.openCertificationDialog(model);
    }

    onRemoveCertification(cert: ExternalPartnerCertification): void {
        this.dialog.confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
            .afterClosed()
            .subscribe(confirmed => {
                if (confirmed) {
                    const index = this.data.acreditations.indexOf(cert);
                    if (index !== -1) {
                        this.data.acreditations.splice(index, 1);
                    }
                }
            });
    }

    onDeleteExternalAttachment(cert: ExternalPartnerCertification, attach: Attachment): void {
        this.dialog.confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
            .afterClosed()
            .subscribe(confirmed => {
                if (confirmed) {
                    const index = cert.attachments.indexOf(attach);
                    if (index !== -1) {
                        cert.attachments.splice(index, 1);
                    }
                }
            });
    }

    onDownloadExternalAttachment(attach: Attachment): void {
        this.profileService.downloadGenericAttachment(this.data.supplierCode, attach.key)
            .subscribe(file => {
                FileSaver.saveAs(file, attach.name);
            });
    }

    onSelectServiceType(option: TslOption): void {
        this.servicetype.setValue(option.value);
    }

    onSelectJobTitle(option: TslOption): void {
        this.jobtitle.setValue(option.value);
    }

    onSelectDepartment(option: TslOption): void {
        this.department.setValue(option.value);
    }

    private openCertificationDialog(cert: any): void {
        const dialogRef = this.matDialog.open(ExternalCertificationDialogComponent, { width: '40%', height: '400px', data: cert });

        dialogRef.afterClosed()
            .subscribe((model: ExternalPartnerCertification) => {
                if (model === undefined) {
                    return;
                }

                if (model.newFiles.length > 0) {
                    forkJoin(model.newFiles.map(file => this.profileService.postGenericAttachment(this.data.supplierCode, file)))
                        .subscribe(attachs => {


                            if (this.attachmentIndex <= -1) {
                                model.attachments = model.attachments.concat(...attachs);
                                this.data.acreditations.push(model);
                            }
                            else {
                                let modelData = this.data.acreditations[this.attachmentIndex];
                                modelData.attachments = modelData.attachments.concat(...attachs);
                                modelData.certificationTypeId = model.certificationTypeId;
                                modelData.certifiedDate = model.certifiedDate;
                                modelData.certifierName = model.certifierName;
                                modelData.expirationDate = model.expirationDate;
                                this.data.acreditations[this.attachmentIndex] = modelData;
                            }
                        });
                } else {
                    if (this.attachmentIndex <= -1) {
                        this.data.acreditations.push(model);
                    }
                    else {
                        let modelData = this.data.acreditations[this.attachmentIndex];
                        modelData.certificationTypeId = model.certificationTypeId;
                        modelData.certifiedDate = model.certifiedDate;
                        modelData.certifierName = model.certifierName;
                        modelData.expirationDate = model.expirationDate;
                        this.data.acreditations[this.attachmentIndex] = modelData;
                    }

                }
            });
    }

    private createForm(): void {
        this.externalForm = this.fb.group({
            company: ['', Validators.required],
            servicetype: ['', Validators.required],
            jobtitle: ['', Validators.required],
            department: ['', Validators.required],
            address1: ['', Validators.required],
            address2: [''],
            city: ['', Validators.required],
            state: [''],
            zipcode: [''],
            country: ['', Validators.required]
        });
    }

    private loadResources(): void {
        this.translate.getAsync('Delete Item')
            .subscribe(t => this.deleteTitle = t);

        this.translate.getAsync('Are you sure you want to delete this item?')
            .subscribe(t => this.deleteConfirmation = t);

        this.service.getJobtitles(this.data.supplierCode).subscribe(res => this.jobtitles = res);
        this.service.getDepartments(this.data.supplierCode).subscribe(res => this.departments = res);
        this.service.getServiceTypes(this.data.supplierCode).subscribe(res => this.serviceTypes = res);
        this.obCountries$ = this.store.pipe(select(SelectCountries));
        this.profileService.getCertificationTypes(this.data.supplierCode).subscribe(res => this.certificationTypes = res);
    }

    getCertificationType(certificationTypeId: number) {
        if (this.certificationTypes) {
            var certificationTypeData = this.certificationTypes.find(x => x.id == certificationTypeId);
            return certificationTypeData ? certificationTypeData.type : "";
        }
        else {
            return "";
        }
    }
}
